import {
  collectLeafNodes,
  getCorrespondingGeoType,
  isLatitudeType,
  isLongitudeType,
  RepresentationStorageFormat,
  type MessagePathNode,
  type TopicNode,
} from "@/shared/domain/topics";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { LoggerService } from "@/shared/services/LoggerService";
import {
  type Dispatch,
  type PanelState,
  type TopicData,
  actions,
  ImagePanelState,
  isImagePanelState,
  isLogPanelState,
  isMapPanelState,
  Layout,
  LayoutItem,
  LayoutOrientation,
  MapPanelState,
  PanelType,
  Placement,
  PlotPanelState,
  RawMessagePanelState,
} from "@/shared/state/visualization";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";
import { columnIdForParts } from "@/shared/state/visualization/schema/v1/log-panel";
import { nanoSecToSecStr } from "@/shared/time";

import { DroppableData, DropZone } from "../DnD";
import {
  findConflictingLogColumn,
  logColumnConflictMessage,
} from "../Panel/LogPanel/getDynamicColumns";
import { panelAcceptsMessagePath } from "../Panel/predicates";

interface LayoutData {
  layout: Layout | LayoutItem;
  orientation: LayoutOrientation;
}

export function canAddToLogPanel(
  panel: PanelState,
  messagePath: MessagePathNode,
): {
  isValid: boolean;
  errors: string[];
} {
  if (!isLogPanelState(panel)) {
    return {
      isValid: false,
      errors: ["Cannot add data to a non-log panel."],
    };
  }

  // Can always add if the panel is empty
  if (panel.data.length === 0) {
    return { isValid: true, errors: [] };
  }

  const topic = messagePath.topic;

  // A Log panel's topic may span multiple files. The same topic gets a distinct
  // id per file, so identity is the name, not the id.
  if (panel.data[0].topic.name !== topic.data.topic_name) {
    return {
      isValid: false,
      errors: ["Cannot add data from a different topic to a log panel."],
    };
  }

  const errors: string[] = [];

  // Require at least one shared column: same-named topics with no columns in
  // common have unrelated contents and must not merge. Key on the same logical
  // column id the rest of the panel uses (columnIdForParts), which is lossless,
  // so distinct columns can't collide and falsely look shared.
  const panelColumnIds = new Set(
    panel.data.map((data) => columnIdForParts(data.messagePath.parts)),
  );
  const overlaps = collectLeafNodes(topic).some((leaf) =>
    panelColumnIds.has(columnIdForParts(leaf.toParts())),
  );
  if (!overlaps) {
    errors.push(
      "Cannot add this topic: it shares a name with the panel's topic but has no message paths in common.",
    );
  }

  if (
    panel.data.some(
      (data) => data.messagePath.id === messagePath.data.message_path_id,
    )
  ) {
    errors.push(
      `Message path "${messagePath.data.message_path}" is already in the log panel.`,
    );
  }

  // Block an overlapping column whose type or categories differ across files:
  // merging would mislabel one file's values.
  const conflict = findConflictingLogColumn(
    messagePath.toTopicData(),
    panel.data,
  );
  if (conflict !== undefined) {
    errors.push(logColumnConflictMessage(conflict.name));
  }

  return { isValid: errors.length === 0, errors };
}

export function canAddToRawMessagePanel(
  panel: RawMessagePanelState,
  messagePath: MessagePathNode,
) {
  // Can always add if the panel is empty
  if (panel.data.length === 0) {
    return { isValid: true, errors: [] };
  }

  const errors: string[] = [];

  const topic = messagePath.topic;

  // Cannot add if the message path is for a different topic
  if (panel.data[0].topic.id !== topic.data.topic_id) {
    errors.push(
      "Cannot add data from a different topic to a raw message panel",
    );
  }

  // Cannot add if the message path is already in the panel
  if (
    panel.data.some(
      (data) => data.messagePath.id === messagePath.data.message_path_id,
    )
  ) {
    errors.push(
      `Message path "${messagePath.data.message_path}" is already in the raw message panel`,
    );
  }

  return { isValid: errors.length === 0, errors };
}

export function canAddToPlotPanel(
  panel: PlotPanelState,
  messagePath: MessagePathNode,
): {
  isValid: boolean;
  errors: string[];
} {
  // The plot reducer appends unconditionally, so dropping an already-plotted
  // message path would silently create a duplicate series.
  if (
    panel.data.some(
      (series) =>
        series.data.messagePath.id === messagePath.data.message_path_id,
    )
  ) {
    return {
      isValid: false,
      errors: [
        `Message path "${messagePath.data.message_path}" is already plotted in this panel.`,
      ],
    };
  }

  return { isValid: true, errors: [] };
}

export function canAddToPanel(panel: PanelState, messagePath: MessagePathNode) {
  const panelType = panel.type;
  switch (panelType) {
    case PanelType.Log:
      return canAddToLogPanel(panel, messagePath);
    case PanelType.RawMessage:
      return canAddToRawMessagePanel(panel, messagePath);
    case PanelType.Plot:
      return canAddToPlotPanel(panel, messagePath);
    default:
      return { isValid: true, errors: [] };
  }
}

/**
 * For a latitude or longitude path, find the sibling path that completes the
 * pair (lat ↔ lon). Map adds bundle the pair into one path, so callers that
 * batch adds need this to know which sibling an add implicitly covers.
 */
export function getCorrespondingMessagePath(messagePath: MessagePathNode) {
  const canonicalType = messagePath.data.canonical_data_type;

  if (isLatitudeType(canonicalType) || isLongitudeType(canonicalType)) {
    const correspondingTypes = getCorrespondingGeoType(canonicalType);
    const siblingMessagePath = messagePath.parent?.children?.find(
      (siblingNode) =>
        correspondingTypes.includes(siblingNode.data.canonical_data_type),
    );

    return siblingMessagePath;
  }

  return undefined;
}

/**
 * On drop of a message path into the panel board,
 * dispatch an action to create a new panel appropriate for that message path.
 */
export function constructPanelForMessagePath(
  dispatch: Dispatch,
  messagePath: MessagePathNode,
  panelType: PanelType,
  onError: (errorMsg: string) => void,
  placement?: Placement,
) {
  const topicData = messagePath.toTopicData();
  let data = [topicData];

  if (panelType === PanelType.Map) {
    const correspondingMessagePath = getCorrespondingMessagePath(messagePath);

    if (correspondingMessagePath) {
      const correspondingData = correspondingMessagePath.toTopicData();
      data = [...data, correspondingData];
    }
  }

  if (
    [PanelType.Image, PanelType.ThreeD].includes(panelType) &&
    !messagePath.hasRepresentation(RepresentationStorageFormat.MCAP)
  ) {
    onError(
      [
        `The ${panelType} Panel does not yet support visualizing this signal.`,
        "Please reach out to Roboto support to let us you know hit this issue.",
      ].join(" "),
    );
    return;
  }

  dispatch(actions.createPanel(data, panelType, placement));
}

function addDataToImagePanel(
  dispatch: Dispatch,
  panelState: ImagePanelState,
  onError: (errorMsg: string) => void,
  data: TopicData,
) {
  if (panelState.data) {
    if (
      panelState.data.some(
        (clip) => clip.data.messagePath.id === data.messagePath.id,
      )
    ) {
      onError("This clip is already used by this panel.");
      return;
    }

    for (const clip of panelState.data) {
      const clipStart = clip.data.topic.startTime;
      const clipEnd = clip.data.topic.endTime;
      const dataStart = data.topic.startTime;
      const dataEnd = data.topic.endTime;

      if (
        clipStart === null ||
        clipStart === undefined ||
        clipEnd === null ||
        clipEnd === undefined ||
        dataStart === null ||
        dataStart === undefined ||
        dataEnd === null ||
        dataEnd === undefined
      ) {
        continue;
      }

      if (
        (clipStart <= dataStart && dataStart <= clipEnd) ||
        (clipStart <= dataEnd && dataEnd <= clipEnd)
      ) {
        LoggerService.warn(
          "Detected overlapping clips. Using first matching clip for overlapped time range. Clip " +
            `t=[${nanoSecToSecStr(BigInt(clipStart))}, ${nanoSecToSecStr(BigInt(clipEnd))}]` +
            " overlaps with existing clip " +
            `t=[${nanoSecToSecStr(BigInt(dataStart))}, ${nanoSecToSecStr(BigInt(dataEnd))}].`,
        );
      }
    }
  }

  dispatch(actions.putImagePanelData(panelState.id, data));
}

function addDataToMapPanel(
  dispatch: Dispatch,
  panelState: MapPanelState,
  messagePath: MessagePathNode,
  onError: (errorMsg: string) => void,
  data: TopicData,
) {
  // Only add the message path to the map if it's not already in use
  // This prevents duplicate paths from being added
  if (!messagePathInMapState(panelState, messagePath)) {
    const correspondingMessagePath = getCorrespondingMessagePath(messagePath);
    const dataArr = correspondingMessagePath
      ? [data, correspondingMessagePath.toTopicData()]
      : [data];

    dispatch(actions.addPathToMapPanel(panelState.id, dataArr));
  } else {
    onError(
      `Message path '${messagePath.data.message_path}' is already in use on the panel.`,
    );
  }
  return;
}

export function addDataToPanel(
  dispatch: Dispatch,
  panelState: PanelState,
  messagePath: MessagePathNode,
  onError: (errorMsg: string) => void,
) {
  const data = messagePath.toTopicData();

  if (panelState.type === PanelType.Plot) {
    dispatch(actions.addSeriesToPlotPanel(panelState.id, data));
    return;
  }

  if (panelState.type === PanelType.RawMessage) {
    dispatch(actions.addDataToRawMessagePanel(panelState.id, data));
    return;
  }

  if (panelState.type === PanelType.Image && isImagePanelState(panelState)) {
    addDataToImagePanel(dispatch, panelState, onError, data);
    return;
  }

  if (panelState.type === PanelType.Log) {
    // Add eligibility is enforced by canAddToPanel upstream, not here.
    dispatch(actions.addDataToLogPanel(panelState.id, data));
    return;
  }

  if (panelState.type === PanelType.Map && isMapPanelState(panelState)) {
    addDataToMapPanel(dispatch, panelState, messagePath, onError, data);
    return;
  }
}

function messagePathInMapState(
  panelState: MapPanelState,
  messagePath: MessagePathNode,
): boolean {
  // Get a list of topic message path ids that are currently in use
  const activeMessagePathIds = panelState.data.flatMap((path) =>
    path.data.map((geoItem) => geoItem.messagePath.id),
  );

  // Check if the provided message path id is already in the list
  if (activeMessagePathIds.includes(messagePath.data.message_path_id)) {
    return true;
  }

  // Message path is not currently in map state
  return false;
}

export function dropMessagePath(
  dispatch: Dispatch,
  draggedMessagePath: MessagePathNode,
  droppable: DroppableData<unknown>,
  onError: (errorMsg: string) => void,
) {
  const { dropZone } = droppable;

  if (dropZone === DropZone.PanelBoard) {
    const panelType = determineDefaultPanelType(draggedMessagePath.data);
    constructPanelForMessagePath(
      dispatch,
      draggedMessagePath,
      panelType,
      onError,
    );
  }
  if (dropZone === DropZone.Layout) {
    const { data } = droppable as DroppableData<LayoutData>;
    const panelType = determineDefaultPanelType(draggedMessagePath.data);
    if (data === undefined) {
      // This would be a programming error.
      ErrorMonitoringService.captureError(
        new Error(
          "DropMessagePath's droppable data is undefined instead of being of type LayoutData",
        ),
      );
      onError("An error occurred dropping data onto the board.");
      return;
    }

    constructPanelForMessagePath(
      dispatch,
      draggedMessagePath,
      panelType,
      onError,
      {
        siblingLayout: data.layout,
        orientation: data.orientation,
      },
    );
  }
  if (dropZone === DropZone.Panel) {
    const { data: currentPanel } = droppable as DroppableData<PanelState>;
    if (currentPanel === undefined) {
      // This would be a programming error.
      ErrorMonitoringService.captureError(
        new Error(
          "DropMessagePath's droppable data is undefined instead of being of type PanelState",
        ),
      );
      onError("An error occurred dropping data on the panel.");
      return;
    }

    if (!panelAcceptsMessagePath(currentPanel, draggedMessagePath)) {
      const msgParts = [
        `Roboto detected "${draggedMessagePath.data.message_path}" as type "${draggedMessagePath.data.canonical_data_type},"`,
        "which is not supported by this panel.",
        "If this type inference is incorrect, it is likely an ingestion issue.",
        "Please reach out to Roboto support for help.",
      ];
      onError(msgParts.join(" "));
      return;
    }

    const addCommand = canAddToPanel(currentPanel, draggedMessagePath);
    if (!addCommand.isValid) {
      onError(addCommand.errors.join("\n"));
      return;
    }

    addDataToPanel(dispatch, currentPanel, draggedMessagePath, onError);
  }
}

export function dropPanel(
  dispatch: Dispatch,
  draggedPanel: LayoutItem,
  droppable: DroppableData<unknown>,
  onError: (errorMsg: string) => void,
) {
  const dropZone = droppable.dropZone;

  if (dropZone === DropZone.Layout) {
    const { data } = droppable as DroppableData<LayoutData>;

    if (data === undefined) {
      // This would be a programming error.
      ErrorMonitoringService.captureError(
        new Error(
          "DropPanel's droppable data is undefined instead of being of type LayoutData",
        ),
      );
      onError("An error occurred dropping this panel.");
      return;
    }

    dispatch(
      actions.movePanel(draggedPanel, {
        siblingLayout: data.layout,
        orientation: data.orientation,
      }),
    );
  }
}

/**
 * Filter a leaf list to the subset whose ids are present in `includeLeafIds`.
 * Both `undefined` and an empty set mean "include everything" — the caller
 * has no narrowing in mind. Treating empty-set as "no filter" stops a
 * future producer that hand-builds an empty Set from silently dropping
 * every leaf at drop time.
 *
 * Logs a warning when `includeLeafIds` references ids that aren't in the
 * subtree (e.g., the tree changed mid-drag). The drop still succeeds for
 * the valid subset — the warning exists so developers can spot stale
 * producers without surfacing it to end users.
 */
function applyLeafIdFilter(
  leaves: MessagePathNode[],
  includeLeafIds: ReadonlySet<string> | undefined,
): MessagePathNode[] {
  if (includeLeafIds === undefined || includeLeafIds.size === 0) {
    return leaves;
  }
  const filtered = leaves.filter((leaf) => includeLeafIds.has(leaf.id));
  if (filtered.length < includeLeafIds.size) {
    LoggerService.warn(
      `applyLeafIdFilter: ${
        includeLeafIds.size - filtered.length
      } of ${includeLeafIds.size} ids were not found in the subtree (drop will proceed with the ${filtered.length} that matched).`,
    );
  }
  return filtered;
}

/**
 * Collect TopicData[] from all leaf descendants of a tree node.
 * Filters out leaves that don't have a valid representation (toTopicData() would throw).
 * When `includeLeafIds` is provided, leaves whose id is not in the set are skipped first
 * (used to honor an active sidebar filter at drop time).
 */
function collectTopicDataFromLeaves(
  node: TopicNode | MessagePathNode,
  onError: (errorMsg: string) => void,
  includeLeafIds?: ReadonlySet<string>,
): TopicData[] {
  const allLeaves = collectLeafNodes(node);
  const leaves = applyLeafIdFilter(allLeaves, includeLeafIds);
  const topicDataList: TopicData[] = [];

  for (const leaf of leaves) {
    try {
      topicDataList.push(leaf.toTopicData());
    } catch {
      // Skip leaves without a valid representation.
      // This can happen if a leaf's representation is missing or unsupported.
      LoggerService.warn(
        `Skipping leaf "${leaf.data.message_path}" — no valid representation`,
      );
    }
  }

  if (topicDataList.length === 0) {
    // Distinguish "filter excluded everything" from "panel can't accept
    // any of these types" so the user knows whether to clear their search
    // or pick a different drop target.
    if (leaves.length === 0 && allLeaves.length > 0) {
      onError(
        "All matching paths were excluded by the active search. Clear or adjust the filter to drop these paths.",
      );
    } else {
      onError("No valid data paths found under this node.");
    }
  }

  return topicDataList;
}

/**
 * On drop of a tree node (parent MessagePathNode or TopicNode) into the panel board or layout,
 * create a new RawMessage panel containing all descendant leaf message paths.
 *
 * All leaves of a tree node share the same topic, so the RawMessage panel's
 * same-topic constraint is always satisfied.
 *
 * When `includeLeafIds` is provided, only leaves whose ids appear in the set
 * are added (used to honor an active sidebar filter at drag time).
 */
export function constructPanelForTreeNode(
  dispatch: Dispatch,
  node: TopicNode | MessagePathNode,
  onError: (errorMsg: string) => void,
  placement?: Placement,
  includeLeafIds?: ReadonlySet<string>,
) {
  const topicDataList = collectTopicDataFromLeaves(
    node,
    onError,
    includeLeafIds,
  );
  if (topicDataList.length === 0) {
    return;
  }

  dispatch(actions.createPanel(topicDataList, PanelType.RawMessage, placement));
}

/**
 * On drop of a tree node onto an existing panel,
 * add all valid descendant leaf message paths to the panel.
 *
 * Uses best-effort semantics: leaves that pass validation are added,
 * leaves that fail (duplicates, wrong topic, unsupported type) are skipped.
 * If ALL leaves fail, an error is shown.
 *
 * When `includeLeafIds` is provided, only leaves whose ids appear in the set
 * are considered (used to honor an active sidebar filter at drag time).
 */
export function addTreeNodeDataToPanel(
  dispatch: Dispatch,
  panelState: PanelState,
  node: TopicNode | MessagePathNode,
  onError: (errorMsg: string) => void,
  includeLeafIds?: ReadonlySet<string>,
) {
  const allLeaves = collectLeafNodes(node);
  const leaves = applyLeafIdFilter(allLeaves, includeLeafIds);

  // The filter narrowed an otherwise-populated subtree down to zero leaves.
  // Distinguish this from "the panel can't accept any of these types" so
  // the user knows their search is stale rather than that the drop target
  // genuinely rejects the data.
  if (leaves.length === 0 && allLeaves.length > 0) {
    onError(
      "All matching paths were excluded by the active search. Clear or adjust the filter to drop these paths.",
    );
    return;
  }

  const errors: string[] = [];
  let addedCount = 0;

  // Track IDs added in this batch to prevent duplicates within a single drop.
  // panelState is captured once before the loop, so after the first dispatch
  // the duplicate check in canAddToPanel runs against stale data.
  const addedInBatch = new Set<string>();

  for (const leaf of leaves) {
    // Check if the panel type accepts this leaf's data type
    if (!panelAcceptsMessagePath(panelState, leaf)) {
      continue; // Silently skip incompatible leaves
    }

    // Check panel-specific validation (duplicates, same-topic, etc.)
    const validation = canAddToPanel(panelState, leaf);
    if (!validation.isValid) {
      errors.push(...validation.errors);
      continue;
    }

    // Skip if this message path was already added in this batch
    if (addedInBatch.has(leaf.data.message_path_id)) {
      continue;
    }

    addDataToPanel(dispatch, panelState, leaf, onError);
    addedInBatch.add(leaf.data.message_path_id);
    addedCount++;
  }

  if (addedCount === 0) {
    if (errors.length > 0) {
      onError(errors[0]); // Show the first error
    } else {
      onError(
        "None of the data paths under this node are compatible with this panel.",
      );
    }
  }
}

/**
 * Handle the drop of a tree node (parent MessagePathNode or TopicNode).
 * Routes to panel creation or panel data addition based on the drop zone.
 *
 * When `includeLeafIds` is provided, drop handlers downstream restrict the
 * subtree to those leaf ids — used to honor an active sidebar filter.
 */
export function dropTreeNode(
  dispatch: Dispatch,
  draggedNode: TopicNode | MessagePathNode,
  droppable: DroppableData<unknown>,
  onError: (errorMsg: string) => void,
  includeLeafIds?: ReadonlySet<string>,
) {
  const { dropZone } = droppable;

  if (dropZone === DropZone.PanelBoard) {
    constructPanelForTreeNode(
      dispatch,
      draggedNode,
      onError,
      undefined,
      includeLeafIds,
    );
  }

  if (dropZone === DropZone.Layout) {
    const { data } = droppable as DroppableData<LayoutData>;
    if (data === undefined) {
      ErrorMonitoringService.captureError(
        new Error(
          "DropTreeNode's droppable data is undefined instead of being of type LayoutData",
        ),
      );
      onError("An error occurred dropping data onto the board.");
      return;
    }

    constructPanelForTreeNode(
      dispatch,
      draggedNode,
      onError,
      {
        siblingLayout: data.layout,
        orientation: data.orientation,
      },
      includeLeafIds,
    );
  }

  if (dropZone === DropZone.Panel) {
    const { data: currentPanel } = droppable as DroppableData<PanelState>;
    if (currentPanel === undefined) {
      ErrorMonitoringService.captureError(
        new Error(
          "DropTreeNode's droppable data is undefined instead of being of type PanelState",
        ),
      );
      onError("An error occurred dropping data on the panel.");
      return;
    }

    addTreeNodeDataToPanel(
      dispatch,
      currentPanel,
      draggedNode,
      onError,
      includeLeafIds,
    );
  }
}
